import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { Check, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { aiNav, aiNavAnchors, knowledgeKinds, knowledgeTips } from "@/mocks/aiassist";
import { goAnchor } from "./app-nav";
import { locale, t, tn } from "@/lib/i18n";
import { BazaAsk, useBazaEnabled } from "./baza-ask";

/* Живой макет «AI Knowledge Sources»: источники знаний для AI Assist.
   Пока список пуст — видно пустое состояние; добавление документов, ссылок и текстов работает в макете. */

type Source = { id: string; name: string; kind: string; meta: string; added: string; status: string };
type Tab = "document" | "link" | "guide" | "api";
type AuthHeader = "bearer" | "key";
type Check = { status: "checking" | "ok" | "fail"; note: string; ms?: number; sample?: string } | null;

const FIELD =
  "h-10 w-full box-border min-w-0 rounded-btn border border-border bg-background px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";

const kindOf = (id: string) => knowledgeKinds.find((k) => k.id === id) ?? knowledgeKinds[0];

function KnowledgeDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const [sources, setSources] = useState<Source[]>([]);
  const bazaOn = useBazaEnabled();
  const [tryBaza, setTryBaza] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("document");
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [body, setBody] = useState("");
  const [file, setFile] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [token, setToken] = useState("");
  const [authHeader, setAuthHeader] = useState<AuthHeader>("bearer");
  const [showToken, setShowToken] = useState(false);
  const [check, setCheck] = useState<Check>(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const fileInput = useRef<HTMLInputElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const checkTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      clearTimeout(timer.current);
      clearTimeout(checkTimer.current);
    },
    [],
  );

  const clearCheck = () => {
    clearTimeout(checkTimer.current);
    setCheck(null);
  };

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4200);
  };

  const onNav = (id: string) => {
    if (id === "ai-assist") {
      goAnchor("#ai-assist");
      return;
    }
    const anchor = aiNavAnchors[id];
    if (anchor) {
      goAnchor(anchor);
      return;
    }
    flash(t("Раздел «{label}» в демо не открывается", { label: aiNav.flatMap((g) => g.items).find((x) => x.id === id)?.label ?? "" }));
  };

  const reset = () => {
    clearCheck();
    setTab("document");
    setName("");
    setUrl("");
    setBody("");
    setFile("");
    setEndpoint("");
    setToken("");
    setAuthHeader("bearer");
    setShowToken(false);
    setError("");
  };

  const openAdd = () => {
    reset();
    setAddOpen(true);
  };

  const pickFile = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;
    setFile(picked.name);
    if (!name.trim()) setName(picked.name.replace(/\.[a-z0-9]+$/i, ""));
    setError("");
  };

  const validate = (): string => {
    if (tab === "document") {
      if (!file) return t("Выберите документ: PDF, DOCX или TXT до 20 МБ");
      return "";
    }
    if (tab === "link") {
      if (!/^https?:\/\/[^\s.]+\.[^\s]{2,}$/i.test(url.trim())) return t("Укажите полную ссылку, начиная с https://");
      if (name.trim().length < 3) return t("Добавьте название — по нему источник ищут в списке");
      return "";
    }
    if (tab === "api") {
      if (!/^https:\/\/[^\s]+\.[^\s]{2,}$/i.test(endpoint.trim())) return t("Укажите адрес API, начиная с https:// — например https://api.hub.ru/v1/knowledge");
      if (token.trim().length < 8) return t("Вставьте токен доступа: без него API не отдаст данные");
      return "";
    }
    if (name.trim().length < 3) return t("Добавьте название источника");
    if (body.trim().length < 20) return t("Вставьте текст-инструкцию: минимум 20 символов, иначе ассистенту нечего читать");
    return "";
  };

  const checkConnection = () => {
    clearTimeout(checkTimer.current);
    const ep = endpoint.trim();
    const tk = token.trim();
    if (!/^https:\/\/[^\s]+\.[^\s]{2,}$/i.test(ep)) {
      setCheck({ status: "fail", note: t("Некорректный адрес — нужен полный URL, начиная с https://") });
      return;
    }
    if (tk.length < 8) {
      setCheck({ status: "fail", note: t("Добавьте токен доступа — без него сервис вернёт 401 Unauthorized") });
      return;
    }
    setCheck({ status: "checking", note: t("Отправляем тестовый запрос к эндпоинту…") });
    checkTimer.current = setTimeout(() => {
      setCheck({
        status: "ok",
        note: t("Соединение установлено — сервис ответил и отдал данные для индексации"),
        ms: 412,
        sample: `{
  "endpoint": "${ep}",
  "status": 200,
  "auth": "${authHeader === "bearer" ? "Authorization: Bearer" : "X-API-Key"}",
  "collection": "knowledge",
  "articles": 1284,
  "updated_at": "2026-10-29T09:12:00Z",
  "items": [
    { "id": "kb-1042", "title": "${t("Регламент возвратов и обмена")}", "updated_at": "2026-10-28" },
    { "id": "kb-1041", "title": "${t("Условия доставки по регионам")}", "updated_at": "2026-10-27" }
  ]
}`,
      });
    }, 900);
  };

  const save = () => {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    const kind = kindOf(tab).id;
    let sourceName = name.trim();
    let meta = "";
    if (tab === "document") {
      sourceName = sourceName || file.replace(/\.[a-z0-9]+$/i, "");
      meta = t("Загружен документ · PDF");
    } else if (tab === "link") {
      meta = url.trim().replace(/^https?:\/\//i, "");
    } else if (tab === "api") {
      const host = endpoint.trim().replace(/^https?:\/\//i, "").split("/")[0];
      sourceName = sourceName || host;
      meta = t("{host} · {auth} · автообновление", { host, auth: authHeader === "bearer" ? t("Bearer-токен") : "X-API-Key" });
    } else {
      meta = t("{n} знаков текста", { n: body.trim().length.toLocaleString(locale) });
    }
    const status = tab === "api" ? t("Синхронизировано") : t("Готово");
    setSources((prev) => [{ id: `ks-${Date.now()}`, name: sourceName, kind, meta, added: "2026-10-29", status }, ...prev]);
    setAddOpen(false);
    reset();
    flash(
      tab === "api"
        ? t("API «{name}» подключён — ассистент прочитает данные и будет обновлять их автоматически", { name: sourceName })
        : t("Источник «{name}» добавлен — AI Assist проиндексирует его и начнёт отвечать по нему", { name: sourceName }),
    );
  };

  const remove = (s: Source) => {
    setSources((prev) => prev.filter((x) => x.id !== s.id));
    flash(t("Источник «{name}» удалён — ассистент больше не будет на него опираться", { name: s.name }));
  };

  const sync = (s: Source) => {
    setSources((prev) => prev.map((x) => (x.id === s.id ? { ...x, status: t("Синхронизировано"), added: "2026-10-29" } : x)));
    flash(t("Запрошено обновление «{name}» — новые данные появятся в течение 5 минут", { name: s.name }));
  };

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: "document", label: t("Документ"), icon: "ri-file-text-line" },
    { id: "link", label: t("Ссылка"), icon: "ri-link" },
    { id: "guide", label: t("Текст"), icon: "ri-quill-pen-line" },
    { id: "api", label: "API", icon: "ri-cloud-line" },
  ];

  const th = "py-2.5 pe-3 text-start font-normal align-middle last:pe-0";
  const rowPad = "py-2.5";

  return (
    <Band id="ai-knowledge" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} / {t("настройки → входящие → AI Assist → источники знаний")}
          </span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {t("Источников: {n}", { n: sources.length + (bazaOn ? 1 : 0) })}
          </span>
        </div>

        <div className="grid lg:grid-cols-[14.5rem_minmax(0,1fr)]">
          {/* ── настройки ── */}
          <div className="hidden flex-col gap-4 border-border p-3 lg:flex lg:border-e">
            {aiNav.map((g) => (
              <div key={g.group} className="flex flex-col gap-0.5">
                <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{g.group}</p>
                {g.items.map((it) => {
                  const active = it.id === "ai-assist";
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onNav(it.id)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12.5px] font-medium transition",
                        active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <i className={cn(it.icon, "text-base leading-none")} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{it.label}</span>
                      {it.badge ? (
                        <span className="rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-600">{it.badge}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="mt-auto rounded-btn border border-border bg-background/60 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <i className="ri-shield-check-line text-sm leading-none" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("Источники знаний загружает администратор. Ассистент использует их, но не меняет документы.")}
              </p>
            </div>
          </div>

          {/* ── источники знаний ── */}
          <div className="min-w-0 p-3.5 md:p-5">
            <div className="flex flex-wrap items-start gap-2.5">
              <button
                type="button"
                onClick={() => onNav("ai-assist")}
                aria-label={t("Назад к AI Assist")}
                className="grid size-8 flex-none cursor-pointer place-items-center rounded-btn border border-border text-muted-foreground transition hover:text-foreground"
              >
                <i className="ri-arrow-left-line" aria-hidden />
              </button>
              <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                <i className="ri-book-open-line text-base leading-none" />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold leading-snug">{t("Источники знаний AI")}</h3>
                <p className="mt-1 max-w-xl text-[12px] leading-relaxed text-muted-foreground">
                  {t("Загружайте документы, ссылки и инструкции или подключайте источники по API — ассистент будет отвечать точнее.")}
                </p>
              </div>
              <div className="flex flex-none items-center gap-2">
                {sources.length > 0 ? (
                  <button
                    type="button"
                    onClick={openAdd}
                    className="inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
                  >
                    <i className="ri-add-line" aria-hidden />
                    {t("Добавить")}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => flash(t("Демо-режим: подробная справка по источникам знаний в макете недоступна"))}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[12px] font-semibold text-primary transition hover:opacity-80"
                >
                  <i className="ri-book-open-line text-sm leading-none" aria-hidden />
                  {t("Узнать больше")}
                </button>
              </div>
            </div>

            <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1 lg:hidden">
              {aiNav.flatMap((g) => g.items).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onNav(it.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    it.id === "ai-assist" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  <i className={it.icon} aria-hidden />
                  {it.label}
                </button>
              ))}
            </div>

            {bazaOn ? (
              <div className="mt-4 rounded-btn border border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="knowledge-baza">
                <div className="flex flex-wrap items-start gap-3">
                  <span className="grid size-10 flex-none place-items-center rounded-btn bg-emerald-500/15 text-emerald-600" aria-hidden>
                    <i className="ri-book-open-line text-xl leading-none" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 text-[13.5px] font-semibold text-foreground">
                      {t("База знаний 3Jar · baza.starec.ai")}
                      <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-600">{t("подключена")}</span>
                    </p>
                    <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                      {t("Прайсы, каталоги, договоры, инвойсы и проформы, реквизиты компании — всё, что загружено в базу знаний. Менеджеры спрашивают её прямо из работы, ничего не копируя:")}
                    </p>
                    <ul className="mt-1.5 flex list-disc flex-col gap-0.5 ps-4 text-[12px] leading-relaxed text-muted-foreground">
                      <li>{t("во «Входящих» — кнопка с книгой рядом с полем ответа: вопрос уходит вместе с последними сообщениями клиента, ответ со ссылками на документы вставляется в ответ одним нажатием;")}</li>
                      <li>{t("в карточке компании — «Спросить базу знаний» в шапке, а во вкладке «Заказы, счета и оплаты» — документы клиента из базы;")}</li>
                      <li>{t("переписка WhatsApp и Telegram сама уходит в базу на оценку — балл виден в шапке диалога.")}</li>
                    </ul>
                    <p className="mt-1.5 text-[11px] text-muted-foreground">
                      {t("База отвечает с правами сотрудника по его рабочей почте и только по документам компании, контрагентов и товаров — без личных дел, окладов и чужих диалогов. Каждый вопрос пишется в журнал.")}
                    </p>
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setTryBaza((v) => !v)}
                        aria-pressed={tryBaza}
                        className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-btn bg-primary px-3 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
                      >
                        <i className="ri-question-answer-line" aria-hidden />
                        {t("Проверить: задать вопрос")}
                      </button>
                      <a
                        href="https://baza.starec.ai"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-8 items-center gap-1.5 rounded-btn border border-border px-3 text-[12px] font-semibold text-foreground hover:bg-muted"
                      >
                        <i className="ri-external-link-line" aria-hidden />
                        {t("Открыть базу знаний")}
                      </a>
                    </div>
                  </div>
                </div>
                {tryBaza ? (
                  <div className="mt-3">
                    <BazaAsk compact onClose={() => setTryBaza(false)} />
                  </div>
                ) : null}
              </div>
            ) : null}

            {sources.length === 0 ? (
              <div className="mt-4 rounded-btn border border-dashed border-border bg-background/40 px-4 py-10 text-center">
                <span className="mx-auto grid size-14 place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                  <i className="ri-book-open-line text-2xl leading-none" />
                </span>
                <p className="mt-3 text-[14px] font-semibold">{t("Источников знаний пока нет")}</p>
                <p className="mx-auto mt-1 max-w-sm text-[12px] leading-relaxed text-muted-foreground">
                  {t("Добавьте источники знаний, чтобы AI Assist отвечал по вашим документам и правилам, а не по общим догадкам.")}
                </p>
                <button
                  type="button"
                  onClick={openAdd}
                  className="mt-3.5 inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-4 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
                >
                  <i className="ri-add-line" aria-hidden />
                  {t("Добавить источник знаний")}
                </button>

                <ul className="mt-6 grid gap-2 text-start sm:grid-cols-2">
                  {knowledgeTips.map((t) => (
                    <li key={t.title} className="rounded-btn border border-border bg-card p-3">
                      <p className="flex items-center gap-1.5 text-[11.5px] font-semibold text-foreground">
                        <i className={cn(t.icon, "text-[13px] leading-none text-primary")} aria-hidden />
                        {t.title}
                      </p>
                      <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{t.text}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[560px] border-collapse text-start">
                  <thead>
                    <tr className="border-b border-border">
                      <th scope="col" className={th}>
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Источник")}</span>
                      </th>
                      <th scope="col" className={cn(th, "hidden sm:table-cell")}>
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Тип")}</span>
                      </th>
                      <th scope="col" className={cn(th, "hidden md:table-cell")}>
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Содержимое")}</span>
                      </th>
                      <th scope="col" className={cn(th, "hidden sm:table-cell")}>
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Добавлен")}</span>
                      </th>
                      <th scope="col" className="w-16 py-2.5 text-end">
                        <span className="sr-only">{t("Действия")}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map((s) => {
                      const k = kindOf(s.kind);
                      return (
                        <tr key={s.id} className="border-b border-border/60 transition last:border-0 hover:bg-muted/40">
                          <td className={cn(rowPad, "pe-3")}>
                            <div className="flex items-center gap-2">
                              <span className="grid size-7 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                                <i className={cn(k.icon, "text-[13px] leading-none")} />
                              </span>
                              <span className="min-w-0">
                                <span className="block truncate text-[13px] font-semibold leading-snug">{s.name}</span>
                                <span className="mt-0.5 block text-[10.5px] text-primary">{s.status}</span>
                              </span>
                            </div>
                          </td>
                          <td className={cn(rowPad, "hidden pe-3 sm:table-cell")}>
                            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-background/70 px-2 py-0.5 text-[11px] font-medium text-foreground/80">
                              <i className={cn(k.icon, "text-[12px] leading-none")} aria-hidden />
                              {k.label}
                            </span>
                          </td>
                          <td className={cn(rowPad, "hidden pe-3 md:table-cell")}>
                            <span className="line-clamp-1 max-w-[18rem] text-[12px] text-muted-foreground">{s.meta}</span>
                          </td>
                          <td className={cn(rowPad, "hidden whitespace-nowrap pe-3 text-[12px] tabular-nums text-muted-foreground sm:table-cell")}>
                            {s.added.split("-").reverse().join(".")}
                          </td>
                          <td className={cn(rowPad, "text-end")}>
                            <span className="inline-flex items-center justify-end gap-1">
                              {s.kind === "api" ? (
                                <button
                                  type="button"
                                  onClick={() => sync(s)}
                                  aria-label={t("Обновить данные из {name}", { name: s.name })}
                                  title={t("Обновить сейчас")}
                                  className="grid size-7 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-primary/10 hover:text-primary"
                                >
                                  <i className="ri-refresh-line text-[14px] leading-none" aria-hidden />
                                </button>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => remove(s)}
                                aria-label={t("Удалить источник {name}", { name: s.name })}
                                className="grid size-7 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-red-500/10 hover:text-red-500"
                              >
                                <i className="ri-delete-bin-line text-[14px] leading-none" aria-hidden />
                              </button>
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
              <span>
                {sources.length === 0
                  ? t("Пока пусто — AI Assist отвечает только по шаблонам")
                  : `${tn(sources.length, "источник", "источника", "источников")} · ${t("проиндексировано")}${
                      sources.some((x) => x.kind === "api") ? ` · ${t("API обновляется сам")}` : ""
                    }`}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-refresh-line text-[13px] leading-none text-primary" aria-hidden />
                {t("Обновление индекса — до 5 минут")}
              </span>
            </div>

            <p className="mt-3 text-[11px] text-muted-foreground">
              {t("Демо-режим: источники добавляются только в макете — проверка соединения имитируется, файлы не загружаются.")}
            </p>
          </div>
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}

        {/* ── окно добавления источника ── */}
        {addOpen ? (
          <div className="absolute inset-0 z-[60] flex items-end justify-center bg-black/65 p-3 backdrop-blur-sm sm:items-center md:p-6">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                save();
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="knowledge-dialog-title"
              className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-card border border-border bg-card p-5 shadow-lift md:p-6"
            >
              <button
                type="button"
                onClick={() => setAddOpen(false)}
                aria-label={t("Закрыть окно источника")}
                className="absolute end-3 top-3 grid size-8 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" aria-hidden />
              </button>

              <div className="flex items-start gap-3 pe-8">
                <span className="grid size-11 flex-none place-items-center rounded-btn bg-primary/10 text-2xl leading-none text-primary" aria-hidden>
                  <i className="ri-book-open-line" />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("Новый источник знаний")}</p>
                  <h3 id="knowledge-dialog-title" className="mt-1 text-lg font-semibold leading-snug">
                    {t("Добавить источник знаний")}
                  </h3>
                </div>
              </div>

              <p className="mt-3 text-[11.5px] leading-relaxed text-muted-foreground">
                {t("Ассистент читает источник и отвечает по нему. Документы, ссылки, тексты и API-подключения можно удалить в любой момент.")}
              </p>

              <div className="mt-3 flex gap-1 overflow-x-auto rounded-btn border border-border bg-background/60 p-1">
                {tabs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setTab(t.id);
                      setError("");
                      clearCheck();
                    }}
                    className={cn(
                      "inline-flex flex-1 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn px-2.5 py-1.5 text-[11.5px] font-semibold transition",
                      tab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <i className={cn(t.icon, "text-[13px] leading-none")} aria-hidden />
                    {t.label}
                  </button>
                ))}
              </div>

              <p className="mt-2 text-[10.5px] text-muted-foreground">{kindOf(tab).hint}</p>

              <div className="mt-3 flex flex-col gap-3">
                {tab === "document" ? (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Документ")}</span>
                    <button
                      type="button"
                      onClick={() => fileInput.current?.click()}
                      className="flex w-full cursor-pointer flex-col items-center gap-1 rounded-btn border border-dashed border-border bg-background/50 px-3 py-5 text-center transition hover:border-primary/60"
                    >
                      <i className="ri-upload-cloud-2-line text-2xl leading-none text-primary" aria-hidden />
                      <span className="text-[12px] font-semibold text-foreground">{file || t("Выбрать файл или перетащить")}</span>
                      <span className="text-[10.5px] text-muted-foreground">{t("PDF, DOCX, TXT · до 20 МБ")}</span>
                    </button>
                    <input ref={fileInput} type="file" accept=".pdf,.docx,.txt" tabIndex={-1} aria-hidden onChange={pickFile} className="hidden" />
                  </div>
                ) : null}

                {tab === "link" ? (
                  <label className="flex min-w-0 flex-col gap-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Ссылка")}</span>
                    <input
                      value={url}
                      onChange={(e) => {
                        setUrl(e.target.value);
                        setError("");
                      }}
                      placeholder="https://hub.example.ru/pricing"
                      inputMode="url"
                      autoComplete="off"
                      spellCheck={false}
                      className={FIELD}
                    />
                  </label>
                ) : null}

                {tab === "guide" ? (
                  <label className="flex min-w-0 flex-col gap-1.5">
                    <span className="flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("Текст-инструкция")}
                      <span className="normal-case tracking-normal tabular-nums">{body.trim().length}/4000</span>
                    </span>
                    <textarea
                      value={body}
                      onChange={(e) => {
                        setBody(e.target.value.slice(0, 4000));
                        setError("");
                      }}
                      rows={5}
                      placeholder={t("Например: возврат оформляем в течение 14 дней. Клиент пишет номер договора и причину, мы высылаем форму заявления.")}
                      className={cn(FIELD, "h-auto min-h-[110px] resize-y py-2.5 leading-relaxed")}
                    />
                  </label>
                ) : null}

                {tab === "api" ? (
                  <>
                    <label className="flex min-w-0 flex-col gap-1.5">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Адрес API (эндпоинт)")}</span>
                      <input
                        value={endpoint}
                        onChange={(e) => {
                          setEndpoint(e.target.value);
                          setError("");
                          clearCheck();
                        }}
                        placeholder="https://api.hub.ru/v1/knowledge"
                        inputMode="url"
                        autoComplete="off"
                        spellCheck={false}
                        className={FIELD}
                      />
                      <span className="text-[10.5px] text-muted-foreground">
                        {t("Метод GET, ответ в JSON или текст. Ассистент будет читать этот адрес по расписанию.")}
                      </span>
                    </label>

                    <div className="grid gap-3 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)]">
                      <label className="flex min-w-0 flex-col gap-1.5">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Заголовок авторизации")}</span>
                        <select
                          value={authHeader}
                          onChange={(e) => {
                            setAuthHeader(e.target.value as AuthHeader);
                            clearCheck();
                          }}
                          className="h-10 w-full box-border min-w-0 cursor-pointer rounded-btn border border-border bg-background px-2.5 text-[13px] text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                        >
                          <option value="bearer">Authorization: Bearer</option>
                          <option value="key">X-API-Key</option>
                        </select>
                      </label>
                      <label className="flex min-w-0 flex-col gap-1.5">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Токен доступа")}</span>
                        <span className="relative block min-w-0">
                          <input
                            value={token}
                            onChange={(e) => {
                              setToken(e.target.value);
                              setError("");
                              clearCheck();
                            }}
                            type={showToken ? "text" : "password"}
                            placeholder="sk_live_••••••••••••"
                            autoComplete="off"
                            spellCheck={false}
                            maxLength={200}
                            className={cn(FIELD, "pe-10 font-mono text-[12.5px]")}
                          />
                          <button
                            type="button"
                            onClick={() => setShowToken((v) => !v)}
                            aria-label={showToken ? t("Скрыть токен") : t("Показать токен")}
                            className="absolute end-1 top-1 grid size-8 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
                          >
                            <i className={cn(showToken ? "ri-eye-off-line" : "ri-eye-line", "text-[15px] leading-none")} aria-hidden />
                          </button>
                        </span>
                      </label>
                    </div>

                    <div className="rounded-btn border border-border bg-background/50 p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={checkConnection}
                          disabled={check?.status === "checking"}
                          className="inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn border border-border bg-card px-3 text-[12px] font-semibold text-foreground transition hover:border-primary/60 hover:text-primary disabled:cursor-wait disabled:opacity-70"
                        >
                          <i
                            className={cn(
                              check?.status === "checking" ? "ri-loader-4-line animate-spin" : "ri-plug-line",
                              "text-[14px] leading-none",
                            )}
                            aria-hidden
                          />
                          {check?.status === "checking" ? t("Проверяем…") : t("Проверить соединение")}
                        </button>
                        {check?.status === "ok" ? (
                          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-600">
                            <i className="ri-checkbox-circle-fill text-[13px] leading-none" aria-hidden />
                            200 OK · {t("{ms} мс", { ms: check.ms })}
                          </span>
                        ) : null}
                        {check?.status === "fail" ? (
                          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-red-500/40 bg-red-500/10 px-2.5 py-1 text-[11px] font-semibold text-red-500">
                            <i className="ri-error-warning-fill text-[13px] leading-none" aria-hidden />
                            {t("Нет соединения")}
                          </span>
                        ) : null}
                      </div>

                      {check ? (
                        <p
                          className={cn(
                            "mt-2 text-[11px] leading-relaxed",
                            check.status === "ok" ? "text-emerald-600" : check.status === "fail" ? "text-red-500" : "text-muted-foreground",
                          )}
                        >
                          {check.note}
                        </p>
                      ) : (
                        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                          {t("Проверим адрес и токен: сервис должен ответить кодом 200 и отдать записи для индексации.")}
                        </p>
                      )}

                      {check?.sample ? (
                        <div className="mt-2.5">
                          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                            <i className="ri-code-s-slash-line text-[12px] leading-none text-primary" aria-hidden />
                            {t("Пример ответа")}
                          </p>
                          <pre className="mt-1.5 max-h-44 overflow-auto whitespace-pre rounded-btn border border-border bg-card p-2.5 font-mono text-[10.5px] leading-relaxed text-foreground/80">
                            {check.sample}
                          </pre>
                          <p className="mt-1.5 text-[10.5px] text-muted-foreground">
                            {t("Проверка ничего не сохраняет — можно менять адрес и токен и проверять снова.")}
                          </p>
                        </div>
                      ) : null}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 rounded-btn border border-border bg-background/60 px-3 py-2.5 text-[11px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-foreground/80">
                        <i className="ri-time-line text-[13px] leading-none text-primary" aria-hidden />
                        {t("Автообновление")}
                      </span>
                      <span className="whitespace-nowrap">{t("каждые 6 часов")}</span>
                      <span className="ms-auto inline-flex items-center gap-1.5 whitespace-nowrap text-primary">
                        <i className="ri-lock-2-line text-[13px] leading-none" aria-hidden />
                        {t("Токен хранится в защищённом виде")}
                      </span>
                    </div>
                  </>
                ) : null}

                <label className="flex min-w-0 flex-col gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Название источника")}</span>
                  <input
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setError("");
                    }}
                    placeholder={t("Например, Регламент возвратов")}
                    autoComplete="off"
                    maxLength={70}
                    className={FIELD}
                  />
                </label>
              </div>

              {error ? <p className="mt-2 text-[11px] text-red-500">{error}</p> : null}

              <p className="mt-3 flex items-start gap-2 rounded-btn border border-border bg-background/60 px-3 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
                <i className="ri-information-line mt-0.5 text-[13px] leading-none text-primary" aria-hidden />
                {t("Документ используется только внутри рабочего пространства. Клиенты не видят исходные файлы — только ответы ассистента.")}
              </p>

              <div className="mt-4 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => setAddOpen(false)}
                  className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-4 text-[13px] font-semibold text-muted-foreground transition hover:text-foreground"
                >
                  {t("Отмена")}
                </button>
                <button
                  type="submit"
                  className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition hover:brightness-110"
                >
                  <i className={tab === "api" ? "ri-plug-line" : "ri-add-line"} aria-hidden />
                  {tab === "api" ? t("Подключить API") : t("Добавить источник")}
                </button>
              </div>
            </form>
          </div>
        ) : null}
      </div>
    </Band>
  );
}

export default KnowledgeDemo;